/**
* ログイン管理
*/

import axios from '@/plugins/axios';
import FirebaseInitializer from '@/plugins/initFirebase';
import router from '@/router';
import cf from '@/mixins/commonFunctions';
import moment from 'moment';

const projectName = process.env.VUE_APP_PROJECTNAME;

// 閲覧にログインが不要のページ名（配下含め）
const nonRequiredLoginPageNames = [
  'login',
  'signup',
  'reset-password',
  'forgot-password',
  'about',
];

/**
 * roleによるログイン可否
 *  11: ドライバー
 *  13: アプリドライバー
 *  21: 特権管理者
 *  22: 開発
 */
const loginAcceptableRoles = [11, 13, 21, 22];

const actions = {
  /** loading開始 */
  showLoading({ dispatch }) {
    dispatch('modal/loadings/showModal', { modalName: 'modalLoadingBallScaleRippleMultiple' }, { root: true });
  },

  /** loading終了 */
  hideLoading({ dispatch }) {
    dispatch('modal/loadings/hideModal', null, { root: true });
  },

  /** ログインしているかのチェック */
  check({ commit, dispatch, state }) {
    const ls = cf.getLocalStorage(projectName);
    // 現在の環境チェック
    dispatch('helper/checkEnv', null, { root: true });
    // ネイティブアプリかの判別
    dispatch('helper/checkIsNativeApp', null, { root: true });

    const currentPath = router.options.history.location;

    // localStorageが存在しない（未サインナップ）
    if (!ls || !ls.auth) {
      commit('changeCheckedStatus', { bool: true, key: 'isLogin' });
      dispatch('user/setUserData', { hasntToken: true }, { root: true });

      const isNotRedirect = nonRequiredLoginPageNames.some((page) => currentPath.includes(page)) || currentPath === '/';

      // ログインページへリダイレクト
      if (!isNotRedirect) {
        alert('本ページを閲覧するにはログインしている必要があります。\nログインページへリダイレクトします。');
        const current = router.options.history;
        const ref = { path: current.location };
        // ログイン後に元いたページへ戻すためpathとqueryを保持しておく
        cf.saveLocalStorage({ loginRef: JSON.stringify(ref) }, projectName);
        dispatch('redirect2Login');
      }

      return;
    }

    // ログインstateがなく、かつauthがあれば認証APIを叩く
    if (!state.isLogin && ls.auth) {
      dispatch('authCheck', {
        actionType: 'check',
        auth: ls.auth,
      });
    }
  },

  /**
   * ログインページのsubmit押下
   * API通信してIDとPASSをpost
   * 認証に成功したらtokenをlocalStorageへ保存
   * @param obj data
   *   loginType:      'default' || 'line'
   *   serialId:       str (default)
   *   password:       str (default)
   */
  submit({ commit, dispatch, rootState }, data) {
    dispatch('showLoading'); /** loading開始 */
    axios({
      method: 'post',
      url: '/v1/user/login',
      data: {
        ...data,
        app_type_name: 'driver',
        device_id: rootState.helper.device.uuid,
      },
    })
      .then((response) => {
        const res = response.data;
        const token = res.token;

        if (res.status === 200 && res.isTempLogin && res.activatekey) {
          router.push({ path: '/signup/password/', query: { activate: res.activatekey } });
        } else if (res.status === 200) {
          const auth = { token, loginType: data.loginType };
          // tokenをlocalStorageに保存
          cf.saveLocalStorage({ auth }, projectName);
          if (['default', 'onlyId'].includes(data.loginType)) dispatch('authCheck', { actionType: 'login', auth });
        } else if (data.loginType === 'default') {
          if (res.message === 'user not found') commit('changeLoginErrorStatus', 1);
        }
        dispatch('hideLoading'); /** loading終了 */
      })
      .catch((error) => {
        dispatch('hideLoading');
        dispatch('loginFailed');
        if (error.response) {
          const errRes = error.response.data;
          console.log(errRes);
          // ログイン失敗時にエラー内容を表示 アカウントなし：1
          // パスワード違い：2
          if (errRes.error.includes('E_USER_NOT_FOUND')) {
            commit('changeLoginErrorStatus', 1);
          } else if (errRes.error.includes('E_INVALID_AUTH_PASSWORD')) {
            commit('changeLoginErrorStatus', 2);
          }

          if (errRes.isBanned) {
            commit('changeBannedStatus', { isBanned: errRes.isBanned, unbannedTime: errRes.unbannedTime });
          } else if (errRes.failedStatus) {
            commit('changeBannedStatus', { isBanned: errRes.failedStatus.isBanned, unbannedTime: errRes.failedStatus.unbannedTime });
          } else {
            commit('changeBannedStatus', { isBanned: false, unbannedTime: null });
          }
        } else console.log(error);
      });
  },


  /**
   * auth情報を使ってログインチェックし
   * 取得したユーザーデータをstateへ格納
   * @param obj data
   *   auth: obj
   *   actionType: 'check' || 'login'
   * logincheckではlocalstorageから
   * loginsubmitでは取得したtokenをdataに格納して渡す
   */
  authCheck({ commit, dispatch, rootState }, data) {
    const ls = cf.getLocalStorage(projectName);
    const Authorization = `${ls.auth?.token?.type} ${ls.auth?.token?.token}`;
    const params = {
      route: 'driver',
      device_id: rootState.helper.device.uuid,
    };

    axios({
      method: 'POST',
      url: '/v1/user/loginCheck',
      headers: { Authorization },
      data: params,
    })
      .then((response) => {
        // user.flagが1以外はログイン無効
        const user = response.data.user;
        if (![1, 2].includes(user.flag)) {
          // ログイン時には失敗の挙動
          if (data.actionType === 'login') dispatch('loginFailed');
          cf.deleteLocalStorage(projectName, 'auth');
          commit('changeLoginStatus', false);
          return;
        }
        if (!(user.role?.role) || !loginAcceptableRoles.includes(user.role.role)) {
          alert('アクセス権限がありません');
          return router.push({ path: '/login/' });
        }

        // チェックOKの場合
        const successParams = {
          loginType: 'default',
          actionType: data.actionType,
          detail: user,
        };

        dispatch('loginSuccess', successParams);

        // プッシュ通知の初期化
        dispatch('initPushNotification', user);
      })
      .catch((error) => {
        if (error.response) console.log(error.response.data);
        else console.log(error);

        if (data.actionType === 'check') {
          // ログインチェック時
          const errors = error.response?.data?.errors;

          // 期限切れ
          if (errors?.[0].message.includes('E_UNAUTHORIZED_ACCESS')) {
            if (ls.auth) { // ログイン画面へリダイレクト
              alert('ログイン有効期限が切れています。\nログイン画面へリダイレクトします');
              const current = router.options.history;
              const ref = { path: current.location };
              // ログイン後に元いたページへ戻すためpathとqueryを保持しておく
              cf.saveLocalStorage({ loginRef: JSON.stringify(ref) }, projectName);
              dispatch('redirect2Login');
              cf.deleteLocalStorage(projectName, 'auth');
            }
          } else if (error.response) {
            // DBとの接続に失敗
            alert('データの取得に失敗しました。リロードをお試しください');
          }
          // error.response がない場合はリクエストがサーバーへ届いていない（通信断など）
          // データの問題ではないうえ、アプリ側の自動リトライでリロードの度にalertが出て
          // ドライバーがOKを押し続ける羽目になるため、ここでは何も表示しない（ログのみ）
        } else if (data.actionType === 'login') {
          // ログイン直後
          dispatch('loginFailed');
          commit('changeLoginStatus', false);
          cf.deleteLocalStorage(projectName, 'auth');
          alert('ログインで問題が発生しました\nお手数ですが管理者へお知らせください');
        }
      })
      .finally(() => {
        commit('changeCheckedStatus', { bool: true, key: 'isLogin' });
      });
  },

  /**
   * プッシュ通知初期化
   */
  async initPushNotification({ dispatch, commit, rootState }, user) { // eslint-disable-line
    let notificationToken = null;
    try {
      if (!rootState.helper.isNative.app) { // native appはスルー
        const firebaseInitializer = new FirebaseInitializer(user, dispatch);
        notificationToken = await firebaseInitializer.init();
      }
    } catch (error) {
      console.error('Failed to initialize Firebase:', error);
    }

    /** tokenが発行されたらbackendでtokensに登録 */
    if (notificationToken && user?.id) {
      const data = {
        foreign_id: user.id,
        name: 'firebase',
        type: 'user',
        token: notificationToken,
        expires_at: moment(new Date()).add(1, 'w').format('YYYY-MM-DD HH:mm:ss'), // 1週間後
      };

      await axios({
        method: 'post',
        url: '/v1/token/create',
        data,
      })
        .then((response) => {
          const res = response.data;
          if (res.isSuccess) commit('setFirebaseToken', notificationToken);
        })
        .catch((error) => {
          console.log(error);
        });
    }

    commit('changeCheckedStatus', { bool: true, key: 'hasToken' });
  },

  /**
   * プッシュ通知tokenの削除
   */
  async deletePushNotificationToken({ rootState }) {
    if (!rootState.user.id) return;
    const data = {
      foreign_id: rootState.user.id,
      name: 'firebase',
      type: 'user',
    };

    axios({
      method: 'post',
      url: '/v1/token/delete',
      data,
    })
      .then(() => {
      })
      .catch((error) => {
        alert('プッシュ通知の削除に失敗しました。\n管理者にお問い合わせください。');
        console.log(error);
      });
  },

  /**
    * ログイン成功時の処理
    * @param {*} data
    *  loginType: 'default' || 'line'
    *  actionType: 'check' || 'login'
    *  detail: obj
    */
  loginSuccess({ commit, dispatch }, data) {
    // login.isFailedをリセット
    commit('changeLoginErrorStatus', 0);
    // loginStatusを更新
    commit('changeLoginStatus', true);
    // ユーザー情報をstate.userにセット（user/index.js）
    dispatch('user/setUserData', data.detail, { root: true });
    // システムマスターの判別
    dispatch('helper/checkSystemMaster', null, { root: true });
    // ログイン制限state解除
    commit('changeBannedStatus', { isBanned: false, unbannedTime: null });

    const ls = cf.getLocalStorage(projectName);
    // リファラの有無でリダイレクト先が変わる
    if (ls && ls.loginRef) {
      const ref = JSON.parse(ls.loginRef);
      const pushParams = { ...ref };
      if (ls.query) pushParams.query = ls.query;
      router.push(pushParams);
      cf.deleteLocalStorage(projectName, 'loginRef');
    } else if (data.actionType === 'login') {
      router.push({ path: '/' });
      // リダイレクトが効かないことがある(ログイン画面に残ってしまう)ので強制リロード
      // setTimeout(() => { router.go('/'); }, 200);
    }
    // state.isLoadingを完了に
    commit('changeCheckedStatus', { bool: true, key: 'isLogin' });
  },

  /** ログイン失敗 */
  loginFailed({ commit }) {
    commit('changeVibrationStatus', true);
    setTimeout(() => { commit('changeVibrationStatus', false); }, 500);
  },


  /** 有効期限切れ時のログイン画面リダイレクト */
  redirect2Login() {
    router.push({ path: '/login/' });
  },


  /** トップへリダイレクト */
  redirect2Top() {
    router.push({ path: '/' });
  },


  /** ログアウト */
  async logout({ dispatch, rootState }) {
    const ls = cf.getLocalStorage(projectName);
    if (ls?.auth?.token) {
      try {
        // ログアウト処理時、ライドシェアドライバーの場合車両の紐付け解除
        if (rootState.user.role?.role === 13) await dispatch('releaseVehicle');
        // ログアウト処理時、ライドシェアドライバーの場合車両の紐付け解除
        if (rootState.user.demandSchedules?.length) await dispatch('releaseDemandSchedule');

        let Authorization = null;
        const data = { user_id: rootState.user.id };
        const isDefault = ['default', 'onlyId'].includes(ls.auth.loginType);
        if (isDefault) Authorization = `${ls.auth.token.type} ${ls.auth.token.token}`;
        const endpoint = isDefault ? 'logout' : 'lineLogout';
        await axios({
          method: 'post',
          url: `/v1/user/${endpoint}`,
          headers: Authorization ? { Authorization } : {},
          data,
        });
        // localStorageの更新
        cf.deleteLocalStorage(projectName, 'loginRef');
        cf.deleteLocalStorage(projectName, 'auth');

        // ログアウト処理時、待機状態なら待機解除（ログアウト処理が完了した後に行わないと自動再登録されてしまうためこのタイミング）
        if (rootState.user.driverStatus.isWaiting) await dispatch('releaseWaitingDriver');

        /** プッシュ通知tokenの削除 */
        dispatch('deletePushNotificationToken');
        // ログインページへリダイレクト・全storeリセットのため強制リロード
        router.push({ path: '/login/' });
        if (rootState.helper.isNative.app) {
          // ネイティブアプリの場合ログアウトした事をアプリに通知
          const native = rootState.helper.isNative;
          const sendData = { action: 'logout' };
          if (native.android) {
            // androidの処理
            const Android = window.Android;
            Android.jsToAndroid(JSON.stringify(sendData));
          } else if (native.iOS) {
            // iOSの処理
            console.log(this.helper.isNative);
          }
        }
        setTimeout(() => { router.go(router.currentRoute.path); }, 500);
      } catch (error) {
        if (error.response) console.log(error.response.data);
        else console.log(error);
        alert('処理中にエラーが発生しました。再度お試しください。');
      }
    }
  },
  async releaseWaitingDriver({ dispatch, rootState }) {
    dispatch('showLoading');
    const data = {
      driver_id: rootState.user.id,
      type: 1,
      update_type: 'release',
    };

    try {
      await axios.post('/v1/waitingDriver/update', data);
    } catch (error) {
      if (error.response) console.log(error.response.data);
      else console.log(error);
      throw error;
    } finally {
      dispatch('hideLoading');
    }
    dispatch('user/update', null, { root: true });
  },
  async releaseVehicle({ dispatch, rootState }) {
    const data = {
      flag: 1,
      user_id: rootState.user.id,
    };

    try {
      await axios.post('/v1/userVehicle/create', data);
    } catch (error) {
      if (error.response) console.log(error.response.data);
      else console.log(error);
      throw error;
    } finally {
      dispatch('hideLoading');
    }
    dispatch('user/update', null, { root: true });
  },
  async releaseDemandSchedule({ dispatch, rootState }) {
    const data = {
      flag: 999,
      demand_schedule_id: rootState.user.demandSchedules[0].id,
      driver_id: rootState.user.id,
    };

    try {
      await axios.post('/v1/demandScheduleDriver/update', data);
    } catch (error) {
      if (error.response) console.log(error.response.data);
      else console.log(error);
      throw error;
    } finally {
      dispatch('hideLoading');
    }
    dispatch('user/update', null, { root: true });
  },
};

const mutations = {
  /** auth || token チェック済みステート */
  changeCheckedStatus(state, { bool, key }) {
    state.isChecked[key] = bool;
  },

  /** ログインステート */
  changeLoginStatus(state, bool) {
    state.isLogin = bool;
  },

  /** ログイン失敗時のバイブフラグステート */
  changeVibrationStatus(state, bool) {
    state.vibration = bool;
  },

  /** ログイン制限時のフラグ・日時ステート */
  changeBannedStatus(state, { isBanned: bool, unbannedTime: datetime }) {
    state.isBanned = bool;
    state.unbannedTime = datetime;
  },

  /** ログイン失敗時のエラー内容ステート */
  changeLoginErrorStatus(state, number) {
    if (number === 0) {
      state.isFaild = 0;
    } else {
      state.isFaild = number;
    }
  },

  setFirebaseToken(state, token) {
    state.firebaseToken = token;
  },
};

const state = {
  type: null,
  isChecked: {
    isLogin: false,
    hasToken: false,
  },
  isLogin: false,
  vibration: false,
  isBanned: false,
  unbannedTime: null,
  isFailed: 0,
  firebaseToken: null,
};


export default {
  namespaced: true,
  actions,
  mutations,
  state,
};
